/* eslint-disable react/prop-types */
import { useParams } from 'react-router-dom';
import {
  format, intervalToDuration, isBefore, parseISO,
} from 'date-fns';
import { useMemo, useState } from 'react';
import { useGlobalFilter, useTable, useFilters } from 'react-table';
import { Button, Popover, Select } from 'antd';
import useQueryReports from '../../hooks/useQueryReports';
import {
  Container, ResultTableData, Row, Table,
} from './styles';
import Loader from '../../components/Loader';
import formatDurationAsTime from '../../utils/formatDurationAsTime';
import useWorkspaces from '../../hooks/useWorkspaces';
import translateResult from '../../utils/translateResult';
import useBackups from '../../hooks/useBackups';

function SelectColumnFilter({
  column: {
    filterValue, setFilter, preFilteredRows, id,
  },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = useMemo(() => {
    const optionsSet = new Set();
    // eslint-disable-next-line react/prop-types
    preFilteredRows.forEach((row) => {
      optionsSet.add(row.values[id]);
    });
    return [...optionsSet.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <select
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">Todos</option>
      {options.map((option) => (
        <option key={`Key_${option}`} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}
export default function ReportsPage() {
  const { backupId } = useParams();
  const { workspaces } = useWorkspaces();
  const { backups } = useBackups();

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(25);

  const { data, isLoading } = useQueryReports({
    backupId, resume: true, limit, offset: offset * limit,
  });

  const columns = useMemo(() => [
    {
      Header: 'Horário',
      accessor: 'endTime',
      disableFilters: true,
    },
    {
      Header: 'Workspace',
      accessor: 'workspace',
      disableFilters: true,
    },
    {
      Header: 'Backup',
      accessor: 'backup',
      disableFilters: true,
    },
    {
      Header: 'Novos Dados',
      accessor: 'newData',
      disableFilters: true,
    },
    {
      Header: 'Dados Modificados',
      accessor: 'modifiedData',
      disableFilters: true,
    },
    {
      Header: 'Duração',
      accessor: 'duration',
      disableFilters: true,
    },
    {
      Header: 'Resultado',
      accessor: 'result',
      Filter: SelectColumnFilter,
    },
  ], []);

  const tableData = useMemo(() => (isLoading ? [] : data.reports.map((report) => {
    const duration = intervalToDuration({
      start: parseISO(report.message.beginTime),
      end: parseISO(report.message.endTime),
    });

    const newData = parseFloat(
      String(report.message.sizeOfAddedFiles / (1024 * 1024)),
    ).toFixed(2);

    const modifiedData = parseFloat(
      String(report.message.sizeOfModifiedFiles / (1024 * 1024)),
    ).toFixed(2);

    return ({
      endTime: format(parseISO(report.message.endTime), 'dd/MM/yyyy HH:mm'),
      workspace: report.backup.workspace.name,
      backup: report.backup.name,
      newData: `${newData} MB`,
      modifiedData: `${modifiedData} MB`,
      duration: formatDurationAsTime(duration),
      result: translateResult(report.message.parsedResult),
      id: report.id,
    });
  })
  ), [data, isLoading]);

  const defaultColumn = useMemo(
    () => ({
      Filter: SelectColumnFilter,
    }),
    [],
  );

  const getRowId = (row, relativeIndex, parent) => (parent
    ? [parent.id, row.id].join('.') : row.id);

  const {
    getTableProps, getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns, data: tableData, defaultColumn, getRowId,
    },
    useFilters,
    useGlobalFilter,
  );
  const isLate = isBefore(
    new Date(backups.find((bkp) => bkp.id === backupId)?.nextReportExpectedAt),
    Date.now(),
  );

  return (
    <Container>
      <h1>
        Relatórios
        {isLate ? (<strong>Este backup possui relatórios em atraso</strong>) : null}
      </h1>
      {isLoading ? <Loader />
        : (
          <Table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      {column.render('Header')}
                      <div>{column.canFilter ? column.render('Filter') : null}</div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);

                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      if (cell.column.Header === 'Resultado') {
                        const isSuccess = cell.value.includes('Bem-sucedido');
                        const isWarning = cell.value.includes('Alerta');
                        const { message: { limitedMessages } } = data.reports
                          .find((report) => report.id === row.id);

                        return isWarning ? (
                          <Popover title="Logs" content={limitedMessages}>
                            <ResultTableData
                              isSuccess={isSuccess}
                              isWarning={isWarning}
                              {...cell.getCellProps()}
                            >
                              {cell.render('Cell')}

                            </ResultTableData>
                          </Popover>
                        )
                          : (
                            <ResultTableData
                              isSuccess={isSuccess}
                              isWarning={isWarning}
                              {...cell.getCellProps()}
                            >
                              {cell.render('Cell')}
                            </ResultTableData>
                          );
                      }

                      if (cell.column.Header === 'Workspace') {
                        const { id } = workspaces
                          .find((workspace) => workspace.name === cell.value);

                        return (
                          <td {...cell.getCellProps()}>
                            <a href={`/workspaces/${id}`}>
                              {cell.render('Cell')}
                            </a>
                          </td>
                        );
                      }

                      return (<td {...cell.getCellProps()}>{cell.render('Cell')}</td>);
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      <Row>
        <Button onClick={() => setOffset((prevState) => prevState - 1)}>
          Anterior
        </Button>
        <Select
          defaultValue={limit}
          onChange={(value) => setLimit(value)}
          options={[
            {
              value: 25,
              label: '25 linhas',
            },
            {
              value: 50,
              label: '50 linhas',
            },
            {
              value: 75,
              label: '75 linhas',
            },
            {
              value: 100,
              label: '100 linhas',
            },
          ]}
        />
        <Button onClick={() => setOffset((prevState) => prevState + 1)}>
          Próximo
        </Button>
      </Row>

    </Container>
  );
}
